import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import pluralGetSet from 'dayjs/plugin/pluralGetSet';
import isoWeek from 'dayjs/plugin/isoWeek';
import duration from 'dayjs/plugin/duration';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import _ from 'lodash';
import { AppConstants } from 'constants/index';
import type { UserConfigs } from 'app/userPreferences/types';
import LocalStorageService from './localStorageService';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(pluralGetSet);
dayjs.extend(isoWeek);
dayjs.extend(duration);
dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(quarterOfYear);

export type DateInput = string | Date | Dayjs | null | undefined;

const DateService = {
  tz: LocalStorageService.get<UserConfigs>('configs')?.timeZone ?? dayjs.tz.guess(),
  date: dayjs,
  dateTz: dayjs.tz,
  setDateTz: (tz: string): void => {
    dayjs.tz.setDefault(tz);
    DateService.tz = tz;
  },

  isValidDate: (date: DateInput): boolean => (!_.isString(date) ? (date as Dayjs | undefined)?.isValid?.() ?? false : DateService.date(date).isValid()),

  getLocalDate: (date: DateInput): Dayjs | null => {
    if (DateService.isValidDate(date)) {
      const data = !_.isString(date) ? (date as Dayjs) : DateService.dateTz(date);
      return data.isUTC() ? data.utc(true) : DateService.date.utc(date as string).tz(DateService.tz);
    }
    return null;
  },

  getFormattedDate: (date: DateInput, format: string = AppConstants.format.date): string | null => DateService.getLocalDate(date)?.format(format) ?? null,

  getFormattedDateByTimeZone: (
    date: DateInput,
    timeZone: string,
    format: string = AppConstants.format.date,
  ): string | null => {
    if (DateService.isValidDate(date)) {
      return DateService.dateTz(date).tz(timeZone).isUTC()
        ? DateService.dateTz(date, timeZone).format(format)
        : dayjs.utc(date as string).tz(timeZone).format(format);
    }
    return null;
  },

  getDateTz: (date?: DateInput): Dayjs => {
    if (DateService.dateTz(date)?.isUTC()) {
      return _.isString(date) || !date
        ? DateService.dateTz(date).utc(true)
        : (date as Dayjs).utc(true);
    }
    if (date && !_.isString(date)) {
      return DateService.date
        .utc(DateService.convertDateObjectToDateString(date as Dayjs))
        .tz(DateService.tz, true);
    }
    return DateService.dateTz(date);
  },

  setFormattedDate: (date: DateInput): Dayjs | null => (DateService.isValidDate(date) ? DateService.getDateTz(date) : null),

  getStartDate: (date?: Dayjs): Dayjs => (date ?? DateService.getDateTz()).startOf('day'),

  getEndDate: (date?: DateInput): Dayjs => (!DateService.date.isDayjs(date) ? DateService.getDateTz(date) : (date as Dayjs))
    .add(1, 'day')
    .startOf('day'),

  getIsoDate: (date: DateInput, keepLocalTime?: boolean): string | null => {
    if (!DateService.isValidDate(date)) {
      return null;
    }
    const data: string = !_.isString(date)
      ? DateService.convertDateObjectToDateString(date as Dayjs)
      : date;
    return DateService.dateTz(data).utc(keepLocalTime).toISOString();
  },

  convertDateObjectToDateString: (date: Dayjs): string => date?.format(AppConstants.format.fullDateTime),

  getDateRanges: (isDateTime: boolean, allowYear?: boolean): Record<string, [Dayjs, Dayjs | null]> => ({
    ...(allowYear && {
      lastYear: [
        DateService.getDateTz().subtract(1, 'year').startOf('year'),
        isDateTime
          ? DateService.getDateTz().startOf('year')
          : DateService.getDateTz().subtract(1, 'year').endOf('year').endOf('month')
            .startOf('day'),
      ],
      thisYear: [DateService.getDateTz().startOf('year'), null],
    }),
    lastMonth: [
      DateService.getDateTz().subtract(1, 'months').startOf('month'),
      isDateTime
        ? DateService.getDateTz().startOf('month')
        : DateService.getDateTz().subtract(1, 'months').endOf('month').startOf('day'),
    ],
    thisMonth: [DateService.getDateTz().startOf('month'), null],
    lastWeek: [
      DateService.getDateTz().subtract(1, 'weeks').startOf('isoWeek'),
      isDateTime
        ? DateService.getDateTz().startOf('isoWeek')
        : DateService.getDateTz().subtract(1, 'weeks').endOf('isoWeek').startOf('day'),
    ],
    thisWeek: [DateService.getDateTz().startOf('isoWeek'), null],
    yesterday: [
      DateService.getDateTz().subtract(1, 'days').startOf('day'),
      isDateTime
        ? DateService.getDateTz().startOf('day')
        : DateService.getDateTz().subtract(1, 'days').startOf('day'),
    ],
    today: [DateService.getDateTz().startOf('day'), null],
  }),
};

export default DateService;
